'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchLeads,
  setLeadsFilters,
  setLeadsPage,
  clearLeads,
  setLeadsFiltersNoPageReset,
} from '../../../redux/features/adminLeadSlice';
import { fetchAgents } from '../../../redux/features/agentSlice';
import moment from 'moment';
import DateSelector from '../HomePage/DateSelector';
import { setGlobalCountries } from '../../../redux/features/countriesSlice';
import {
  useCountries,
  useVisaTypeFilters,
} from '../../client/dashboard/staticData';
import { reportColumnsByType, reportConfig } from './staticData';
import LeadExportDialog from './LeadExportDialog';
import LeadTopControls from './LeadTopControls';
import LeadExportFilterControl from './LeadExportFilterControl';
import {
  buildReportCsv,
  buildRawReportFromFullData,
  ensureFileExtension,
  getSafeReportFileNameBase,
  triggerBlobDownload,
  triggerCsvDownload,
  triggerPdfDownloadFromRows,
} from './leadExportUtils';
import { LeadApplicationsView, LeadPagination } from './LeadWorkspaceSections';

const statusFilters = [
  'All',
  'Active/In Progress',
  'Pending',
  'Payment Pending',
  'Transaction Approval Pending',
  'Visa Applied',
  'FCC (Re-open)',
  'ART (Re-open)',
];

export default function MainLeads({ basePath = '/admin' }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const countries = useCountries();
  const visaTypeFilters = useVisaTypeFilters();
  const [processTypes, setProcessTypes] = useState([]);
  const [selectedVisaExpiryDays, setSelectedVisaExpiryDays] = useState('');
  const [selectedProcessType, setSelectedProcessType] = useState('');
  const visaExpiryDayOptions = useMemo(
    () => Array.from({ length: 365 }, (_, index) => index + 1),
    [],
  );
  const { leads, isLoading, error, totalPages, filters } = useSelector(
    (state) => state.leads,
  );
  const { agents } = useSelector((state) => state.agents);
  const activeAgents = useMemo(
    () => (agents || []).filter((agent) => agent.isActive),
    [agents],
  );
  const globalCountries = useSelector(
    (state) => state.globalCountries.globalCountries,
  );
  const [isGridView, setIsGridView] = useState(false);
  const hasFetchedInitially = useRef(false);
  const initialFiltersApplied = useRef(false);
  const pageUrlSynced = useRef(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  const queryCategory = useMemo(
    () => new URLSearchParams(location.search).get('visaCategory'),
    [location.search],
  );
  const {
    stateFromMonth,
    stateFromYear,
    stateToMonth,
    stateToYear,
    stateCategory,
    stateStatus,
  } = location.state || {};
  const incomingCategory = stateCategory || queryCategory;
  const [selectedCategory, setSelectedCategory] = useState(
    incomingCategory || 'All',
  );

  const computeInitialStatus = () => {
    if (stateStatus === 'Payment') return 'Payment Pending';
    if (
      stateStatus === 'Visa' ||
      stateStatus === 'VisaApplied' ||
      stateStatus === 'Visa Applied'
    )
      return 'Visa Applied';
    if (stateStatus === 'Active' || stateStatus === 'Active/In Progress')
      return 'Active/In Progress';
    if (stateStatus === 'Pending') return 'Pending';
    return 'All';
  };
  const [selectedStatus, setSelectedStatus] = useState(computeInitialStatus());
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [fromMonth, setFromMonth] = useState(stateFromMonth ?? null);
  const [fromYear, setFromYear] = useState(stateFromYear ?? null);
  const [toMonth, setToMonth] = useState(stateToMonth ?? null);
  const [toYear, setToYear] = useState(stateToYear ?? null);
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [isAgentDropdownOpen, setIsAgentDropdownOpen] = useState(false);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const agentDropdownRef = useRef(null);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [exportReportType, setExportReportType] = useState('');
  const [exportCountries, setExportCountries] = useState([]);
  const [exportAgents, setExportAgents] = useState([]);
  const [exportStatus, setExportStatus] = useState('');
  const [exportVisaCategory, setExportVisaCategory] = useState('');
  const [exportMatchField, setExportMatchField] = useState('passport');
  const [exportDays, setExportDays] = useState('');
  const [exportBiometricFromDate, setExportBiometricFromDate] = useState('');
  const [exportBiometricToDate, setExportBiometricToDate] = useState('');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  const [exportDateError, setExportDateError] = useState('');
  const [exportDownloadFormat, setExportDownloadFormat] = useState('csv');
  const [isExporting, setIsExporting] = useState(false);
  const [exportLoadingText, setExportLoadingText] =
    useState('Generating Report');
  const [currentUserRole, setCurrentUserRole] = useState('');
  const [currentUserId, setCurrentUserId] = useState('');
  const autoAgentFilterAppliedRef = useRef('');
  const todayDate = moment().format('YYYY-MM-DD');
  const canExportReports = useMemo(
    () =>
      ['admin', 'super-admin', 'superadmin', 'sub-admin', 'subadmin'].includes(
        (currentUserRole || '').toLowerCase(),
      ),
    [currentUserRole],
  );
  const isAdminUser = (currentUserRole || '').toLowerCase() === 'admin';
  const exportReportConfig = useMemo(() => {
    return reportConfig.filter(
      (report) => isAdminUser || report.reportType !== 'raw-application-data',
    );
  }, [isAdminUser]);

  const fetchLeadsData = () => {
    dispatch(fetchLeads({}));
  };

  useEffect(() => {
    try {
      const userData = localStorage.getItem('user');
      const parsedUser = userData ? JSON.parse(userData) : null;
      setCurrentUserRole(parsedUser?.role || '');
      setCurrentUserId(parsedUser?._id || parsedUser?.id || '');
    } catch (error) {
      setCurrentUserRole('');
      setCurrentUserId('');
    }
  }, []);

  useEffect(() => {
    if (agents.length === 0) {
      dispatch(fetchAgents());
    }
  }, [agents.length, dispatch]);

  useEffect(() => {
    if (incomingCategory) return;

    setSearchInput('');
    setSearchTerm('');
    setSelectedCategory('All');
    setSelectedStatus('All');
    setSelectedCountries([]);
    setFromMonth(null);
    setFromYear(null);
    setToMonth(null);
    setToYear(null);
    setSelectedVisaExpiryDays('');
    setSelectedProcessType('');
    setSelectedAgents([]);
    dispatch(setGlobalCountries([]));
    autoAgentFilterAppliedRef.current = '';
  }, [incomingCategory, dispatch]);

  useEffect(() => {
    if (incomingCategory && incomingCategory !== selectedCategory) {
      setSelectedCategory(incomingCategory);
    }
  }, [incomingCategory, selectedCategory]);

  useEffect(() => {
    const normalizedRole = String(currentUserRole || '').toLowerCase().trim();
    const isSubAdminRole = normalizedRole === 'sub-admin' || normalizedRole === 'subadmin';

    if (!isSubAdminRole || !incomingCategory || !currentUserId) return;

    const routeKey = `${location.pathname}${location.search}|${incomingCategory}`;
    if (autoAgentFilterAppliedRef.current === routeKey) return;

    setSelectedAgents([currentUserId]);
    dispatch(setLeadsPage(1));
    autoAgentFilterAppliedRef.current = routeKey;
  }, [
    currentUserRole,
    currentUserId,
    incomingCategory,
    location.pathname,
    location.search,
    dispatch,
  ]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isAgentDropdownOpen &&
        agentDropdownRef.current &&
        !agentDropdownRef.current.contains(event.target)
      ) {
        setIsAgentDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAgentDropdownOpen]);

  useEffect(() => {
    if (!hasFetchedInitially.current) {
      dispatch(fetchLeads({}));
      hasFetchedInitially.current = true;
      return;
    }

    if (location.state?.refresh) {
      dispatch(fetchLeads({}));
    }
  }, [location.state]);

  useEffect(() => {
    const fetchProcessTypes = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setProcessTypes([]);
          return;
        }
        const response = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/processtype`,
          {
            method: 'GET',
            headers: { authorization: `bearer ${token}` },
          },
        );
        const data = await response.json();
        setProcessTypes(data?.body?.processTypes || []);
      } catch (error) {
        console.error('Error fetching process types:', error);
        setProcessTypes([]);
      }
    };
    fetchProcessTypes();
  }, []);

  const handleDateChange = (type, value) => {
    switch (type) {
      case 'fromMonth':
        setFromMonth(value);
        break;
      case 'fromYear':
        setFromYear(value);
        break;
      case 'toMonth':
        setToMonth(value);
        break;
      case 'toYear':
        setToYear(value);
        break;
      default:
        break;
    }
  };

  const handleCheckboxChange = (countryName) => {
    setSelectedCountries((prev) => {
      const updated = prev.includes(countryName)
        ? prev.filter((c) => c !== countryName)
        : [...prev, countryName];
      dispatch(setGlobalCountries(updated));
      return updated;
    });
  };

  useEffect(() => {
    const sameSelection =
      selectedCountries.length === globalCountries.length &&
      selectedCountries.every((country, index) => country === globalCountries[index]);

    if (sameSelection) return;

    setSelectedCountries(globalCountries);
    // Don't reset page here - let the filter effect handle pagination reset if needed
    // dispatch(setLeadsPage(1));
  }, [globalCountries, selectedCountries]);

  const handleAgentToggle = (agentId) => {
    setSelectedAgents((prev) =>
      prev.includes(agentId)
        ? prev.filter((id) => id !== agentId)
        : [...prev, agentId],
    );
  };

  const clearAgentSelection = () => setSelectedAgents([]);

  const CustomDateSelector = () => (
    <DateSelector
      className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto"
      fromMonth={fromMonth}
      setFromMonth={(v) => handleDateChange('fromMonth', v)}
      fromYear={fromYear}
      setFromYear={(v) => handleDateChange('fromYear', v)}
      toMonth={toMonth}
      setToMonth={(v) => handleDateChange('toMonth', v)}
      toYear={toYear}
      setToYear={(v) => handleDateChange('toYear', v)}
    />
  );

  const fromDateStr = useMemo(() => {
    if (!fromYear || !fromMonth) return '';
    return `${fromYear}-${String(fromMonth).padStart(2, '0')}-01`;
  }, [fromYear, fromMonth]);

  const toDateStr = useMemo(() => {
    if (!toYear || !toMonth) return '';
    const endDay = moment(`${toYear}-${toMonth}`, 'YYYY-M').daysInMonth();
    return `${toYear}-${String(toMonth).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;
  }, [toYear, toMonth]);

  useEffect(() => {
    const t = setTimeout(() => setSearchTerm(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const mapStatusToApi = (ui) => {
    switch (ui) {
      case 'All':
        return 'All';
      case 'Active/In Progress':
        return 'Active/In Progress';
      case 'Pending':
        return 'Pending';
      case 'Payment Pending':
        return 'Payment Pending';
      case 'Transaction Approval Pending':
        return 'Transaction Approval Pending';
      case 'Visa Applied':
        return 'VisaApplied';
      case 'VisaApplied':
        return 'VisaApplied';
      case 'FCC (Re-open)':
        return 'FCC (Re-open)';
      case 'ART (Re-open)':
        return 'ART (Re-open)';
      default:
        return ui;
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      const visa = selectedCategory;
      const apiStatus = mapStatusToApi(selectedStatus);

      const filterPayload = {
        fromDate: fromDateStr,
        toDate: toDateStr,
        countries: selectedCountries,
        visaCategory: visa,
        status: apiStatus,
        visaExpiryDays: selectedVisaExpiryDays,
        processType: selectedProcessType,
        searchTerm: searchTerm,
        agentIds: selectedAgents,
      };

      // During initial mount, don't reset page to preserve URL page param
      // After initial apply, use regular setLeadsFilters which resets page on filter change
      if (!initialFiltersApplied.current && !pageUrlSynced.current) {
        dispatch(setLeadsFiltersNoPageReset(filterPayload));
      } else if (!initialFiltersApplied.current) {
        // First time but page already synced from URL, preserve it
        dispatch(setLeadsFiltersNoPageReset(filterPayload));
      } else {
        // Subsequent changes by user, reset page to 1
        dispatch(setLeadsFilters(filterPayload));
      }

      if (!initialFiltersApplied.current) initialFiltersApplied.current = true;
    }, 600); // debounce time

    return () => clearTimeout(handler);
  }, [
    fromDateStr,
    toDateStr,
    selectedCountries,
    selectedCategory,
    selectedVisaExpiryDays,
    selectedProcessType,
    selectedStatus,
    searchTerm,
    selectedAgents,
  ]);

  useEffect(() => {
    if (!initialFiltersApplied.current) return;
    dispatch(fetchLeads({}));
  }, [
    dispatch,
    filters.fromDate,
    filters.toDate,
    filters.countries,
    filters.visaCategory,
    filters.visaExpiryDays,
    filters.processType,
    filters.status,
    filters.searchTerm,
    filters.agentIds,
    filters.page,
    filters.limit,
  ]);

  // Sync page from URL on mount and on navigation back
  useEffect(() => {
    const pageParam = searchParams.get('page');
    if (pageParam && !isNaN(pageParam)) {
      const page = parseInt(pageParam, 10);
      // Only dispatch if different to avoid potential re-renders or loops
      if (page !== filters.page) {
        dispatch(setLeadsPage(page));
      }
    } else if (filters.page !== 1) {
      // If no page param in URL, ensure page is 1
      dispatch(setLeadsPage(1));
    }
    pageUrlSynced.current = true;
    return () => {
      // Don't clear leads to preserve state for back navigation
      // dispatch(clearLeads());
    };
  }, [searchParams]); // Listen to searchParams to detect back navigation

  // Sync page from Redux to URL
  useEffect(() => {
    const currentUrlPage = parseInt(searchParams.get('page') || '1', 10);
    const reduxPage = filters.page || 1;

    if (currentUrlPage !== reduxPage) {
      setSearchParams(
        (prev) => {
          prev.set('page', reduxPage.toString());
          return prev;
        },
        { replace: false }, // Push to history so back button works for pagination
      );
    }
  }, [filters.page, setSearchParams, searchParams]);

  const handleApplicationClick = (id) => {
    navigate(`${basePath}/leads/${id}`);
  };
  const handlePageChange = (newPage) => {
    const current = filters.page || 1;
    const max = totalPages || 1;
    const target = Math.min(Math.max(newPage, 1), max);
    if (target !== current) dispatch(setLeadsPage(target));
  };

  const handleExportReportTypeChange = (value) => {
    setExportReportType(value);
    setExportCountries([]);
    setExportAgents([]);
    setExportStatus('');
    setExportVisaCategory('');
    setExportMatchField('passport');
    setExportDays('');
    setExportBiometricFromDate('');
    setExportBiometricToDate('');
    setExportDateError('');
  };

  const handleExportFromDateChange = (value) => {
    if (!value) {
      setExportFromDate('');
      setExportDateError('');
      return;
    }

    const selectedDate = moment(value, 'YYYY-MM-DD', true);
    if (!selectedDate.isValid()) return;

    if (selectedDate.isAfter(moment(), 'day')) {
      setExportDateError('From date cannot be in the future.');
      return;
    }

    if (
      exportToDate &&
      selectedDate.isAfter(moment(exportToDate, 'YYYY-MM-DD', true), 'day')
    ) {
      setExportDateError('From date cannot be later than To date.');
      return;
    }

    setExportFromDate(value);
    setExportDateError('');
  };

  const handleExportToDateChange = (value) => {
    if (!value) {
      setExportToDate('');
      setExportDateError('');
      return;
    }

    const selectedDate = moment(value, 'YYYY-MM-DD', true);
    if (!selectedDate.isValid()) return;

    if (
      exportFromDate &&
      selectedDate.isBefore(moment(exportFromDate, 'YYYY-MM-DD', true), 'day')
    ) {
      setExportDateError('To date cannot be earlier than From date.');
      return;
    }

    setExportToDate(value);
    setExportDateError('');
  };

  const handleExportBiometricFromDateChange = (value) => {
    if (!value) {
      setExportBiometricFromDate('');
      setExportDateError('');
      return;
    }

    const selectedDate = moment(value, 'YYYY-MM-DD', true);
    if (!selectedDate.isValid()) return;

    if (
      exportBiometricToDate &&
      selectedDate.isAfter(
        moment(exportBiometricToDate, 'YYYY-MM-DD', true),
        'day',
      )
    ) {
      setExportDateError(
        'Biometric From Date cannot be later than Biometric To Date.',
      );
      return;
    }

    setExportBiometricFromDate(value);
    setExportDateError('');
  };

  const handleExportBiometricToDateChange = (value) => {
    if (!value) {
      setExportBiometricToDate('');
      setExportDateError('');
      return;
    }

    const selectedDate = moment(value, 'YYYY-MM-DD', true);
    if (!selectedDate.isValid()) return;

    if (
      exportBiometricFromDate &&
      selectedDate.isBefore(
        moment(exportBiometricFromDate, 'YYYY-MM-DD', true),
        'day',
      )
    ) {
      setExportDateError(
        'Biometric To Date cannot be earlier than Biometric From Date.',
      );
      return;
    }

    setExportBiometricToDate(value);
    setExportDateError('');
  };

  const selectedExportReport = exportReportConfig.find(
    (report) => report.reportType === exportReportType,
  );

  const resetExportDialogFields = () => {
    setExportReportType('');
    setExportCountries([]);
    setExportAgents([]);
    setExportStatus('');
    setExportVisaCategory('');
    setExportMatchField('passport');
    setExportDays('');
    setExportBiometricFromDate('');
    setExportBiometricToDate('');
    setExportFromDate('');
    setExportToDate('');
    setExportDateError('');
    setExportDownloadFormat('csv');
    setExportLoadingText('Generating Report');
  };

  const handleExport = async () => {
    if (!canExportReports) {
      alert('Only super-admin or sub-admin can generate reports.');
      return;
    }

    if (!exportReportType) {
      alert('Please select Category / Format');
      return;
    }

    if (exportDateError) {
      alert(exportDateError);
      return;
    }

    if (
      exportFromDate &&
      exportToDate &&
      moment(exportFromDate, 'YYYY-MM-DD', true).isAfter(
        moment(exportToDate, 'YYYY-MM-DD', true),
        'day',
      )
    ) {
      alert('To date cannot be earlier than From date.');
      return;
    }

    if (
      exportBiometricFromDate &&
      exportBiometricToDate &&
      moment(exportBiometricFromDate, 'YYYY-MM-DD', true).isAfter(
        moment(exportBiometricToDate, 'YYYY-MM-DD', true),
        'day',
      )
    ) {
      alert('Biometric To Date cannot be earlier than Biometric From Date.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      alert('Authentication token not found');
      return;
    }

    const isRawApplicationDataExport =
      exportReportType === 'raw-application-data';
    const apiReportType = exportReportType;

    const payload = {
      reportType: apiReportType,
      format: exportDownloadFormat,
    };
    const columns =
      reportColumnsByType[apiReportType] ||
      reportColumnsByType['country-wise-clients'];

    const activeFilters = selectedExportReport?.filters || [];

    if (activeFilters.includes('countries') && exportCountries.length > 0) {
      payload.countries = exportCountries;
    }
    if (activeFilters.includes('agents') && exportAgents.length > 0) {
      payload.agents = exportAgents;
    }
    if (activeFilters.includes('visaCategory') && exportVisaCategory) {
      payload.visaCategory = exportVisaCategory;
    }
    if (activeFilters.includes('status') && exportStatus) {
      const mappedStatus = mapStatusToApi(exportStatus);
      if (mappedStatus && mappedStatus !== 'All') {
        payload.status = mappedStatus;
      }
    }
    if (activeFilters.includes('days') && exportDays) {
      payload.daysToExpiry = Number(exportDays);
    }
    if (activeFilters.includes('matchField') && exportMatchField) {
      payload.matchField = exportMatchField;
    }
    if (
      activeFilters.includes('biometricFromDate') &&
      exportBiometricFromDate
    ) {
      payload.biometricFromDate = exportBiometricFromDate;
    }
    if (activeFilters.includes('biometricToDate') && exportBiometricToDate) {
      payload.biometricToDate = exportBiometricToDate;
    }
    if (exportFromDate) {
      payload.fromDate = exportFromDate;
    }
    if (exportToDate) {
      payload.toDate = exportToDate;
    }

    try {
      setIsExporting(true);
      setExportLoadingText('Generating Report');
      const params = new URLSearchParams();

      Object.entries(payload).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;

        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item !== undefined && item !== null && item !== '') {
              params.append(key, String(item));
            }
          });
          return;
        }

        params.append(key, String(value));
      });

      const queryString = params.toString();
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/application/generateReport${queryString ? `?${queryString}` : ''}`,
        {
          method: 'GET',
          headers: {
            authorization: `bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        let message = 'Failed to generate report';
        try {
          const errorData = await response.json();
          message = errorData?.message || errorData?.error || message;
        } catch {
          // Keep fallback message if error body is not JSON
        }
        alert(message);
        return;
      }

      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        setExportLoadingText('Downloading Report');
        await new Promise((resolve) => setTimeout(resolve, 0));

        const defaultReportRows = Array.isArray(data?.body?.data)
          ? data.body.data
          : [];
        const { rows: rawReportRows, columns: rawColumns } =
          buildRawReportFromFullData(data?.body?.data);
        const shouldUseRawDataRows =
          isRawApplicationDataExport && rawColumns.length > 0;
        const reportRows = shouldUseRawDataRows ? rawReportRows : defaultReportRows;
        const finalColumns = shouldUseRawDataRows ? rawColumns : columns;
        const safeFileBase = getSafeReportFileNameBase(
          data?.body?.title,
          exportReportType,
        );
        const fileName = `${safeFileBase}-${Date.now()}`;

        if (exportDownloadFormat === 'pdf') {
          await triggerPdfDownloadFromRows({
            rows: reportRows,
            fileName,
            title: data?.body?.title,
            columns: finalColumns,
          });
        } else {
          const csvContent = buildReportCsv(reportRows, finalColumns);
          triggerCsvDownload(csvContent, ensureFileExtension(fileName, 'csv'));
        }

        setIsExportDialogOpen(false);
        resetExportDialogFields();
        return;
      }

      setExportLoadingText('Downloading Report');
      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const fileNameMatch = disposition.match(
        /filename\*?=(?:UTF-8'')?"?([^";]+)/i,
      );
      const defaultExtension =
        exportDownloadFormat === 'pdf' || contentType.includes('application/pdf')
          ? 'pdf'
          : 'csv';
      const suggestedFileName =
        fileNameMatch?.[1] ||
        `${getSafeReportFileNameBase('', exportReportType)}-${Date.now()}.${defaultExtension}`;

      triggerBlobDownload(
        blob,
        ensureFileExtension(decodeURIComponent(suggestedFileName), defaultExtension),
      );
      setIsExportDialogOpen(false);
      resetExportDialogFields();
    } catch (err) {
      alert(err?.message || 'Something went wrong while exporting');
    } finally {
      setIsExporting(false);
      setExportLoadingText('Generating Report');
    }
  };

  const renderExportFilter = (filterName) => {
    return (
      <LeadExportFilterControl
        filterName={filterName}
        countries={countries}
        activeAgents={activeAgents}
        exportCountries={exportCountries}
        setExportCountries={setExportCountries}
        exportAgents={exportAgents}
        setExportAgents={setExportAgents}
        exportStatus={exportStatus}
        setExportStatus={setExportStatus}
        exportVisaCategory={exportVisaCategory}
        setExportVisaCategory={setExportVisaCategory}
        visaTypeFilters={visaTypeFilters}
        exportMatchField={exportMatchField}
        setExportMatchField={setExportMatchField}
        selectedExportReport={selectedExportReport}
        exportDays={exportDays}
        setExportDays={setExportDays}
        exportBiometricFromDate={exportBiometricFromDate}
        handleExportBiometricFromDateChange={handleExportBiometricFromDateChange}
        exportBiometricToDate={exportBiometricToDate}
        handleExportBiometricToDateChange={handleExportBiometricToDateChange}
        statusFilters={statusFilters}
      />
    );
  };

  return (
    <div className="md:p-6">
      <LeadTopControls
        canExportReports={canExportReports}
        onOpenExport={() => setIsExportDialogOpen(true)}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
        visaTypeFilters={visaTypeFilters}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        statusFilters={statusFilters}
        selectedVisaExpiryDays={selectedVisaExpiryDays}
        setSelectedVisaExpiryDays={setSelectedVisaExpiryDays}
        selectedProcessType={selectedProcessType}
        setSelectedProcessType={setSelectedProcessType}
        processTypes={processTypes}
        agentDropdownRef={agentDropdownRef}
        isAgentDropdownOpen={isAgentDropdownOpen}
        setIsAgentDropdownOpen={setIsAgentDropdownOpen}
        selectedAgents={selectedAgents}
        activeAgents={activeAgents}
        handleAgentToggle={handleAgentToggle}
        clearAgentSelection={clearAgentSelection}
        customDateSelector={<CustomDateSelector />}
      />

      {/* Error */}
      {/* {error && (
        <div className="text-center text-red-600 mb-2">{String(error)}</div>
      )} */}

      {isExporting && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/55 px-4 backdrop-blur-sm">
          <div className="flex items-center gap-4 rounded-2xl bg-white px-6 py-4 shadow-2xl">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-[#005787] border-t-transparent" />
            <span className="text-sm font-medium text-gray-700">
              {exportLoadingText}
            </span>
          </div>
        </div>
      )}

      <LeadExportDialog
        isOpen={isExportDialogOpen}
        isExporting={isExporting}
        exportLoadingText={exportLoadingText}
        exportDateError={exportDateError}
        todayDate={todayDate}
        exportFromDate={exportFromDate}
        onFromDateChange={handleExportFromDateChange}
        exportToDate={exportToDate}
        onToDateChange={handleExportToDateChange}
        exportReportType={exportReportType}
        onReportTypeChange={handleExportReportTypeChange}
        reportConfig={exportReportConfig}
        selectedExportReport={selectedExportReport}
        renderExportFilter={renderExportFilter}
        exportDownloadFormat={exportDownloadFormat}
        setExportDownloadFormat={setExportDownloadFormat}
        onExport={handleExport}
        onClose={() => {
          if (isExporting) return;
          setExportDateError('');
          setIsExportDialogOpen(false);
        }}
      />

      <LeadApplicationsView
        isLoading={isLoading}
        leads={leads}
        isGridView={isGridView}
        onApplicationClick={handleApplicationClick}
      />

      <LeadPagination
        totalPages={totalPages}
        currentPage={filters.page}
        onPageChange={handlePageChange}
        isLoading={isLoading}
        limit={filters.limit || 10}
        onLimitChange={(v) => {
          dispatch(setLeadsFilters({ ...filters, limit: v }));
          dispatch(setLeadsPage(1));
        }}
      />
    </div>
  );
}
